/**
 * Record-payment — PURE money-gate resolution + classification (money-module
 * SLICE 2). The impure Firestore seam (read prior payments, write a partial
 * ledger entry, invoke the deployed `markPaidManual`) lives in
 * `recordPaymentStore.ts`; THIS module is pure (no Firebase, no React) so the
 * Node test runner exercises the full decision logic directly.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  THE ONE MONEY AUTHORITY (locked — do NOT regress)
 * ─────────────────────────────────────────────────────────────────────────
 *  `projects/{pid}.gateState.moneyPaid[gateId]` is the SINGLE release
 *  authority. The deployed `markPaidManual` Cloud Function flips it (admin
 *  SDK), writes the immutable `payments/{id}` ledger entry, and stops the
 *  reminder loop — all atomically. We NEVER introduce a second paid-flag.
 *
 *  CONSEQUENCE for PARTIAL payments: `markPaidManual` clears the gate on ANY
 *  positive amount — it has no notion of "part-paid". So a PARTIAL payment
 *  must NOT call it (that would wrongly release the deliverable). A partial
 *  records a `payments/{id}` ledger doc WITHOUT clearing the gate; the gate
 *  stays open; the running balance is the gate's expected amount minus the
 *  sum of partial payments recorded against it. Only when the running total
 *  reaches the gate's expected amount do we call `markPaidManual` to clear
 *  the gate (the final settling payment). This keeps the gate as the sole
 *  authority while honouring the mockup's partial/full + running-balance UX.
 *
 *  OVERRIDE-SETTLEMENT: a gate already released-unpaid by an admin override
 *  (the gate is cleared, but a `payment_reminders` doc is "chasing" the
 *  balance) is SETTLED by recording the payment — `markPaidManual` records
 *  the ledger + stops the reminder, and does NOT re-release (the gate is
 *  already cleared, so flipping it again is a no-op). We surface this as a
 *  distinct "settlement" intent so the UI shows the reconciliation copy.
 *
 *  CURRENCY: `markPaidManual`'s amount field is named `amountINR` but is the
 *  numeric amount in the PROJECT's currency (the CF does no FX). We pass the
 *  entered amount through verbatim; the firm/currency steer routing + display
 *  only, never the figure (one pricing path — the LOCKED engagement gates).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  MULTI-CURRENCY SETTLEMENT — "received in USD, settled in INR at the bank"
 *  (2026-06-14 — owner: VP672 / C00469 Tarveen paid USD, Kotak credits INR)
 * ─────────────────────────────────────────────────────────────────────────
 *  A foreign-currency payment has TWO money facts that must BOTH be recorded:
 *    (a) the amount the client actually PAID, in the received currency (USD);
 *    (b) the INR amount the bank actually CREDITED after forex conversion —
 *        the SETTLEMENT (bank/SWIFT fees are absorbed into this figure; there
 *        is NO separate fee field in v1).
 *  The EFFECTIVE rate is DERIVED, never typed:  fxRate = settledINR ÷ receivedFx.
 *
 *  WHAT DRIVES WHAT (do NOT conflate):
 *    • The money-GATE math uses figure (a) — the amount in the gate's own
 *      currency (USD for a USD engagement). The USD expected is NEVER
 *      FX-converted; a USD payment clears its USD gate on the USD amount.
 *    • Figure (b), the INR credited, is a BANKING/ACCOUNTING fact recorded
 *      ALONGSIDE the payment (fields `amountSettledINR` + `fxRate`). It does
 *      NOT feed the gate, the balance, or the USD receivable total — INR and
 *      USD are never summed (moneyModel keeps them separate).
 *
 *  RECEIVED CURRENCY at entry: the user picks the currency the client PAID
 *  in (USD / INR) — defaulting to the project currency but selectable, so a
 *  USD engagement can record a USD receipt + its INR bank credit. An
 *  INR-received payment is unchanged: received == settled, no FX step, no
 *  second figure (`fxRate` absent / 1, `amountSettledINR` absent).
 *
 *  The `amountINR` field on the CF/ledger doc is OVERLOADED legacy naming —
 *  it holds the gate-currency amount (a). The NEW fields disambiguate:
 *    currencyReceived : 'INR' | 'USD'  — what the client paid in.
 *    amountReceivedFx : number         — same as `amountINR` (the gate-currency
 *                                         amount); named for clarity.
 *    amountSettledINR : number | null  — INR credited to the bank (USD only).
 *    fxRate           : number | null  — derived effective rate (USD only).
 * ─────────────────────────────────────────────────────────────────────────
 */
import {
  getEngagementTemplate,
  resolveGateAmount,
  seedGateAmounts,
  type Tier,
} from './engagementTemplate';
import { firmFor, type Currency, type FirmId } from './firms';

/* ════════════════════ FX / settlement (PURE) ════════════════════ */

/**
 * The settlement facts for ONE payment. For an INR-received payment the
 * settlement equals the receipt (no FX) — `settledINR` is null and `fxRate`
 * is null. For a USD-received payment the client paid `receivedFx` USD and
 * the bank credited `settledINR` rupees; the EFFECTIVE rate is derived.
 */
export interface SettlementFx {
  /** Currency the client actually PAID in. */
  currencyReceived: Currency;
  /** Amount received in that currency (the gate-currency amount). */
  receivedFx: number;
  /** INR credited to the bank after forex (USD payments only; else null). */
  settledINR: number | null;
  /** Derived effective rate settledINR ÷ receivedFx (USD only; else null). */
  fxRate: number | null;
}

/**
 * Effective FX rate = INR credited ÷ amount received. Bank/forex fees are
 * absorbed into this rate (no separate fee field). Returns null when it
 * cannot be computed (zero/negative received, or no INR figure). Rounded to
 * 4 dp — enough precision for a per-payment effective rate.
 */
export function effectiveRate(
  receivedFx: number,
  settledINR: number | null | undefined,
): number | null {
  if (!(receivedFx > 0)) return null;
  if (settledINR == null || !(settledINR > 0)) return null;
  return Math.round((settledINR / receivedFx) * 10000) / 10000;
}

/**
 * Build the settlement record for a payment. `currencyReceived === 'INR'`
 * (or anything not USD): received == settled, no FX — `settledINR`/`fxRate`
 * are null (the figure already IS rupees; recording a redundant settlement
 * would just double it). USD: the caller supplies the INR credited; the rate
 * is derived. A USD payment with no INR figure yet (settledINR null) is a
 * legal "awaiting bank credit" record — rate null until the credit lands.
 */
export function settlementOf(
  currencyReceived: Currency,
  receivedFx: number,
  settledINR: number | null,
): SettlementFx {
  if (currencyReceived !== 'USD') {
    return {
      currencyReceived,
      receivedFx,
      settledINR: null,
      fxRate: null,
    };
  }
  const credited =
    typeof settledINR === 'number' && settledINR > 0 ? settledINR : null;
  return {
    currencyReceived: 'USD',
    receivedFx,
    settledINR: credited,
    fxRate: effectiveRate(receivedFx, credited),
  };
}

/* ════════════════════ Method by currency ════════════════════ */

/**
 * UI payment methods (per the mockup) split by currency. INR → Bank · UPI ·
 * Cash · Cheque. USD → Wire · PayPal · Stripe · Card. The chips switch with
 * the project's currency so staff never pick an impossible method.
 */
export type UiMethod =
  | 'bank'
  | 'upi'
  | 'cash'
  | 'cheque'
  | 'wire'
  | 'paypal'
  | 'stripe'
  | 'card';

/** The CF's constrained method vocabulary (`VALID_PAY_METHODS`). */
export type CfMethod = 'cash' | 'bank_transfer' | 'cheque' | 'upi' | 'other';

/** The methods offered for a currency (mockup §A). */
export function methodsFor(currency: Currency): UiMethod[] {
  return currency === 'USD'
    ? ['wire', 'paypal', 'stripe', 'card']
    : ['bank', 'upi', 'cash', 'cheque'];
}

/** Human label for a UI method chip. */
export function methodLabel(m: UiMethod): string {
  switch (m) {
    case 'bank':
      return 'Bank';
    case 'upi':
      return 'UPI';
    case 'cash':
      return 'Cash';
    case 'cheque':
      return 'Cheque';
    case 'wire':
      return 'Wire';
    case 'paypal':
      return 'PayPal';
    case 'stripe':
      return 'Stripe';
    case 'card':
      return 'Card';
  }
}

/**
 * Map a UI method to the deployed CF's constrained vocabulary. Only Cash /
 * UPI / Cheque map 1:1; Bank → `bank_transfer`; every USD rail (Wire/PayPal/
 * Stripe/Card) maps to `other` (the CF has no USD-rail enum) while the human
 * label is preserved in the payment's `reference`/note for the ledger.
 */
export function cfMethodFor(m: UiMethod): CfMethod {
  switch (m) {
    case 'cash':
      return 'cash';
    case 'upi':
      return 'upi';
    case 'cheque':
      return 'cheque';
    case 'bank':
      return 'bank_transfer';
    case 'wire':
    case 'paypal':
    case 'stripe':
    case 'card':
      return 'other';
  }
}

/* ════════════════════ Gate enumeration ════════════════════ */

/**
 * A money gate of a project, resolved for the record-payment picker. Carries
 * the expected amount (from the LOCKED engagement template — one pricing
 * path), the cleared flag, and the running balance from prior partial
 * payments. `state` drives the picker row colour + selectability.
 */
export interface PayableGate {
  id: string;
  label: string;
  /** Expected amount in the project's currency (numeric, no FX). */
  expected: number;
  /** Already cleared in `gateState.moneyPaid` (the sole authority). */
  cleared: boolean;
  /** Sum of partial payments recorded against this gate so far. */
  paidSoFar: number;
  /** expected − paidSoFar (≥0). 0 when fully covered or cleared. */
  balance: number;
  /**
   * Is this gate currently released-unpaid by an admin OVERRIDE? (cleared,
   * yet still has an outstanding balance being chased.) Drives the
   * "settlement" intent + the violet reconciliation copy.
   */
  overrideOpen: boolean;
  /**
   * Picker state:
   *   'paid'     — cleared AND fully covered → not selectable (settled).
   *   'override' — cleared but balance chasing (override) → selectable to
   *                SETTLE.
   *   'due'      — not cleared, blocks delivery → the primary target.
   *   'partial'  — not cleared, some partial paid → continue paying.
   */
  state: 'paid' | 'override' | 'due' | 'partial';
}

/**
 * The minimal project shape the gate enumeration needs — fed from a live
 * project row plus its raw `gateState.moneyPaid` map (the sole authority).
 */
export interface PayContextProject {
  pid: string;
  vp: string;
  clientName: string;
  clientNo: string;
  clientId: string;
  projectName: string;
  tierCode: Tier;
  valueINR: number;
  currency: Currency;
  /** The `gateState.moneyPaid` boolean map off the project doc. */
  moneyPaid: Record<string, boolean>;
  /**
   * AGREED-PRICE — the per-gate expected amounts seeded from the agreed price
   * (the $40 fix), in the engagement currency. THIS is what was missing: the
   * Record-a-payment modal was building its gate amounts from the template
   * ONLY (ignoring the agreed override) → it showed ₹11,000 while the project
   * page already showed $40. Now the SAME canonical `resolveGateAmount` reads
   * this map, so the modal expects the agreed figure. Empty/undefined →
   * template defaults (the un-negotiated standing-rate path).
   */
  gateAmounts?: Record<string, number>;
}

/**
 * A prior `payments/{id}` ledger row, narrowed to what the running-balance
 * math needs. BOTH partial ledger rows (written by this slice) AND the CF's
 * gate-clearing payment rows (written by `markPaidManual`) count toward
 * `paidSoFar` — the `partial` flag only distinguishes how the row was
 * created, not whether its money counts. (It was a bug to count only
 * partials: a gate cleared by a FULL payment would then look unpaid.)
 */
export interface PriorPayment {
  gateId: string;
  amount: number;
  /** True for a ledger-only partial; false for a CF gate-clearing payment. */
  partial: boolean;
}

/**
 * Resolve every money gate of a project into a `PayableGate`, folding in the
 * prior partial payments so the running balance is exact. PURE — the caller
 * supplies the prior payments it already read. Amounts come ONLY from the
 * LOCKED engagement template (fixed `amountINR`, or `fraction × valueINR`);
 * never invented.
 */
export function payableGates(
  project: PayContextProject,
  priorPayments: PriorPayment[],
  /**
   * Outstanding-balance signal from ACTIVE `payment_reminders` keyed by
   * gateId — a gate released-unpaid by an admin override seeds a reminder
   * that chases the balance. When a gate appears here with a positive
   * amount, it is treated as override-open even if no payment row exists
   * (the override may have recorded `amountPaid:0`). The
   * `recordGateOverride` CF is the only writer of these. Optional — omitted
   * → override-open is inferred from the payment shortfall only.
   */
  chasingByGate: Record<string, number> = {},
): PayableGate[] {
  const tpl = getEngagementTemplate(project.tierCode);
  if (!tpl) return [];

  // Sum EVERY recorded payment per gate — partial ledger rows AND the CF's
  // gate-clearing payment rows both count as money received against the gate.
  const paidByGate = new Map<string, number>();
  for (const p of priorPayments) {
    paidByGate.set(p.gateId, (paidByGate.get(p.gateId) ?? 0) + p.amount);
  }

  return tpl.gates
    .filter(
      (g): g is Extract<typeof g, { kind: 'money' }> => g.kind === 'money',
    )
    .map((g) => {
      // THE ONE CANONICAL RESOLVER — agreed `gateAmounts` override wins over
      // the template default. This is the fix for the screenshot bug: the
      // modal now expects the agreed $40, not the template ₹11,000. Same
      // function the money summary + project-page gate row call.
      const expected = resolveGateAmount(g, project.valueINR, project.gateAmounts);
      const cleared = project.moneyPaid[g.id] === true;
      const paidSoFar = paidByGate.get(g.id) ?? 0;
      const shortfall = Math.max(0, expected - paidSoFar);
      // An ACTIVE reminder on this gate (override release-unpaid) is the
      // authoritative "chasing" signal — its outstanding amount, when given.
      const chasing = chasingByGate[g.id];
      const hasChase = typeof chasing === 'number' && chasing > 0;
      // A CLEARED gate is override-open when it is still being chased:
      // either an active reminder exists (the reliable signal) OR money was
      // recorded but falls short of the expected amount. A clean full clear
      // (no reminder, no shortfall) is simply paid.
      const overrideOpen = cleared && (hasChase || (paidSoFar > 0 && shortfall > 0));
      // Balance: an uncleared gate owes its shortfall; a cleared gate owes
      // nothing UNLESS override-open — then it owes the chased amount (the
      // reminder's outstanding figure wins, else the payment shortfall).
      const balance = cleared
        ? overrideOpen
          ? hasChase
            ? chasing
            : shortfall
          : 0
        : shortfall;
      let state: PayableGate['state'];
      if (cleared) state = overrideOpen ? 'override' : 'paid';
      else state = paidSoFar > 0 ? 'partial' : 'due';
      return {
        id: g.id,
        label: g.label,
        expected,
        cleared,
        paidSoFar,
        balance,
        overrideOpen,
        state,
      };
    });
}

/** A gate is selectable to record a payment against unless it is fully
 *  settled (cleared + zero balance). */
export function gateSelectable(g: PayableGate): boolean {
  return g.state !== 'paid';
}

/* ════════════════════ Payment classification ════════════════════ */

export type PaymentIntent = 'full' | 'partial' | 'settlement';

export interface RecordPaymentDraft {
  gate: PayableGate;
  /** The amount the user entered, in the project's currency. */
  amount: number;
  /** Full vs partial — the user's toggle (settlement is derived). */
  mode: 'full' | 'partial';
}

export interface PaymentPlan {
  /** What this record does to the gate + reminder. */
  intent: PaymentIntent;
  /**
   * Does this payment CLEAR the gate (call `markPaidManual`)? true for a
   * full payment and for a partial that completes the balance; false for a
   * partial that leaves a balance.
   */
  clearsGate: boolean;
  /** Balance remaining on the gate AFTER this payment (≥0). */
  balanceAfter: number;
  /** Was the gate already released-unpaid via override (settlement path)? */
  settlesOverride: boolean;
  /** Human one-liner for the consequence callout. */
  consequence: string;
  /** Validation problem, or null when the draft is recordable. */
  error: string | null;
}

/**
 * THE decision. From the user's draft, classify the payment and decide
 * whether it clears the gate (and thus calls `markPaidManual`) or is a
 * partial ledger-only record. PURE + total — every branch returns a plan
 * (an invalid draft returns a plan with `error` set, never throws).
 *
 *  Rules:
 *   • amount must be > 0 and ≤ the gate's outstanding balance (no over-pay).
 *   • settlement (gate already override-open): recording ANY amount up to
 *     the balance records the ledger + (when it completes the balance) stops
 *     the chase. We treat a settlement that completes the balance as
 *     clearing (markPaidManual stops the reminder); a partial settlement is
 *     ledger-only and the chase continues.
 *   • full mode: amount is the outstanding balance → clears the gate.
 *   • partial mode: if the amount completes the balance it is promoted to a
 *     clearing payment (so the gate doesn't get stuck open at exactly 0);
 *     otherwise it is ledger-only and the gate stays open.
 */
export function planPayment(draft: RecordPaymentDraft): PaymentPlan {
  const { gate, amount, mode } = draft;
  const outstanding = gate.balance;

  if (!(amount > 0)) {
    return invalidPlan(gate, 'Enter the amount received.');
  }
  if (amount > outstanding + 0.5) {
    return invalidPlan(
      gate,
      `Amount exceeds the outstanding balance (${outstanding}). Record at most the balance.`,
    );
  }

  const completes = amount >= outstanding - 0.5; // float-safe "covers it"
  const balanceAfter = Math.max(0, Math.round(outstanding - amount));

  // Override-open gate → this is a SETTLEMENT (no re-release).
  if (gate.overrideOpen) {
    const clears = completes;
    return {
      intent: 'settlement',
      clearsGate: clears,
      balanceAfter,
      settlesOverride: true,
      consequence: clears
        ? 'Settles the admin override — records the payment, closes the balance, ' +
          'and stops the chasing reminder. The deliverable was already released, ' +
          'so nothing new is released.'
        : 'Partial settlement of the override — records the payment and reduces the ' +
          `chased balance to ${balanceAfter}. The reminder continues until it clears.`,
      error: null,
    };
  }

  // Normal (not-yet-cleared) gate.
  if (mode === 'full' || completes) {
    return {
      intent: completes && mode === 'partial' ? 'partial' : 'full',
      clearsGate: true,
      balanceAfter: 0,
      settlesOverride: false,
      consequence:
        'Records the payment AND clears the money-gate — the deliverable is ' +
        'released to the client. (Wraps markPaidManual.)',
      error: null,
    };
  }

  // Partial that leaves a balance → ledger-only, gate stays open.
  return {
    intent: 'partial',
    clearsGate: false,
    balanceAfter,
    settlesOverride: false,
    consequence:
      `Records a part-payment. The gate stays OPEN — the deliverable is NOT ` +
      `released until the remaining ${balanceAfter} is paid. Status: "Partly paid".`,
    error: null,
  };
}

function invalidPlan(gate: PayableGate, error: string): PaymentPlan {
  return {
    intent: 'full',
    clearsGate: false,
    balanceAfter: gate.balance,
    settlesOverride: gate.overrideOpen,
    consequence: '',
    error,
  };
}

/* ════════════════════ markPaidManual input builder ════════════════════ */

/**
 * The exact `MarkPaidInput` shape the deployed callable expects (mirrors
 * gateOverride.ts `MarkPaidInput`). Built here so the wiring is pure +
 * tested; the impure seam only invokes the callable.
 */
export interface MarkPaidCall {
  projectId: string;
  gateKind: 'money';
  gateId: string;
  /** CF field is `amountINR` (numeric amount in the GATE/project currency). */
  amountINR: number;
  method: CfMethod;
  reference: string;
  /**
   * MULTI-CURRENCY settlement fields (2026-06-14). The CURRENTLY-DEPLOYED
   * `markPaidManual` ignores unknown fields — these are forward-compatible:
   * the prepared CF patch (`avbp_gate_override.fxsettle.patch`, NOT yet
   * deployed) reads them and persists them on the CF-written `payments/` doc.
   * Until that patch lands, the impure seam stamps them onto the doc via a
   * follow-up write (see recordPaymentStore.submitPayment). All are OMITTED
   * for an INR-received payment (received == settled, no FX).
   */
  currencyReceived?: Currency;
  /** Amount received in `currencyReceived` (= amountINR; named for clarity). */
  amountReceivedFx?: number;
  /** INR credited to the bank after forex (USD only). */
  amountSettledINR?: number;
  /** Derived effective rate settledINR ÷ received (USD only). */
  fxRate?: number;
}

/**
 * Build the `markPaidManual` payload from a clearing payment. ONLY called
 * when `plan.clearsGate` — a partial-that-leaves-a-balance never invokes the
 * CF. The reference preserves the human method label + currency so a USD
 * rail (mapped to CF `other`) is still legible in the ledger. `amountINR` is
 * the GATE-currency amount (USD for a USD gate) — the CF does NO FX; the INR
 * credited rides as the optional settlement fields.
 */
export function buildMarkPaidCall(
  project: PayContextProject,
  gate: PayableGate,
  amount: number,
  uiMethod: UiMethod,
  reference: string,
  settlement?: SettlementFx,
): MarkPaidCall {
  const ref = [
    `${methodLabel(uiMethod)} · ${project.currency}`,
    reference.trim(),
  ]
    .filter(Boolean)
    .join(' · ')
    .slice(0, 200);
  const call: MarkPaidCall = {
    projectId: project.pid,
    gateKind: 'money',
    gateId: gate.id,
    amountINR: amount,
    method: cfMethodFor(uiMethod),
    reference: ref,
  };
  // Attach settlement metadata only for a USD-received payment (INR receipts
  // need no FX trail — received already equals settled).
  if (settlement && settlement.currencyReceived === 'USD') {
    call.currencyReceived = 'USD';
    call.amountReceivedFx = settlement.receivedFx;
    if (settlement.settledINR != null) call.amountSettledINR = settlement.settledINR;
    if (settlement.fxRate != null) call.fxRate = settlement.fxRate;
  }
  return call;
}

/**
 * The FX/settlement fields to persist on a `payments/` doc — shared by both
 * write paths (the CF-clearing follow-up stamp AND the partial ledger body).
 * Returns an EMPTY object for an INR-received payment (nothing to stamp).
 */
export function settlementFields(
  settlement: SettlementFx | undefined,
): Record<string, unknown> {
  if (!settlement || settlement.currencyReceived !== 'USD') return {};
  const out: Record<string, unknown> = {
    currencyReceived: 'USD',
    amountReceivedFx: settlement.receivedFx,
  };
  if (settlement.settledINR != null) out.amountSettledINR = settlement.settledINR;
  if (settlement.fxRate != null) out.fxRate = settlement.fxRate;
  return out;
}

/**
 * The body of a PARTIAL `payments/{id}` ledger doc (ledger-only — does NOT
 * clear the gate). Mirrors the CF's payment shape so the consolidated
 * statement reads both kinds uniformly. `at` is filled with serverTimestamp
 * by the impure caller. The `partial:true` flag is what `payableGates` reads
 * to compute the running balance — and what the tighter firestore.rule
 * requires (a client-SDK payments write must be a partial, never a
 * gate-clearing one; clearing only happens via the admin-SDK CF).
 */
export function partialPaymentBody(
  project: PayContextProject,
  gate: PayableGate,
  amount: number,
  uiMethod: UiMethod,
  reference: string,
  actorUid: string,
  actorName: string,
  settlement?: SettlementFx,
): Record<string, unknown> {
  return {
    projectId: project.pid,
    clientId: project.clientId || project.clientNo || null,
    // `amountINR` = the GATE-currency amount (USD for a USD gate) — the field
    // name is legacy; `currency` records what unit it actually is.
    amountINR: amount,
    currency: project.currency,
    method: cfMethodFor(uiMethod),
    uiMethod,
    reference: reference.trim().slice(0, 200),
    channel: 'offline_manual',
    gateKind: 'money',
    gateId: gate.id,
    // The flag the running-balance math + the firestore.rule key on.
    partial: true,
    // MULTI-CURRENCY settlement (USD receipts only — empty spread for INR).
    // The partial path is a client-SDK create, so these land on the doc
    // directly (the rule permits extra fields beyond its required shape).
    ...settlementFields(settlement),
    recordedByUid: actorUid,
    recordedByName: actorName,
  };
}

/* ════════════════════ AGREED PRICE (the $40 fix · PURE) ════════════════════ */

/**
 * The current agreed-price state of a project (what the live-fee control reads).
 */
export interface AgreedPriceState {
  currency: Currency | null;
  value: number | null;
}

/** The input for an agreed-price edit on a live project. */
export interface AgreedPriceInput {
  tier: Tier | string;
  /** New currency the fee is agreed in. */
  currency: Currency;
  /** New agreed amount in `currency` units (e.g. 40 for $40). */
  value: number;
  /** The PRIOR agreed state (drives the change-history was→now). */
  prior: AgreedPriceState;
  /** GST flag override; defaults INR→true, USD→false when omitted. */
  gst?: boolean;
  /** Optional short note logged with the change. */
  note?: string;
  /** Who is making the change (display name / email). */
  actorName: string;
  /** ISO timestamp of the change (deterministic — caller supplies). */
  now: string;
}

/**
 * The result of an agreed-price edit — the project-doc field patch + the
 * change-history row to append. PURE + deterministic so the Node test runner
 * can prove the $40 walk without Firebase.
 *
 * THE FIX: `gateAmounts` is seeded from the agreed `value` in its own currency
 * (₹ or $), so the money gate expects the agreed figure — NOT the template
 * ₹11,000 wearing a "$" sign. `currency` is written so the money module renders
 * the right symbol. A change row records was→now/by/when (money is never
 * changed silently — MOCKUP_3).
 */
export interface AgreedPriceUpdate {
  /** Bounded project-doc field patch (no gateState — ordinary fields only). */
  patch: {
    currency: Currency;
    valueINR: number;
    firmId: FirmId;
    gst: boolean;
    gateAmounts: Record<string, number>;
  };
  /** The change-history row to append (newest-last). */
  historyEntry: {
    at: string;
    by: string;
    fromCurrency: Currency | null;
    fromValue: number | null;
    toCurrency: Currency;
    toValue: number;
    note?: string;
  };
  /**
   * NO-OP GUARD (2026-06-16 — Bug B fix). True when the edit changes nothing
   * (the new currency AND amount equal the prior agreed currency AND amount).
   * The owner saw a meaningless "was $40 → now $40" row; the store skips the
   * `feeHistory` append (and the redundant write) when this is true.
   */
  noop: boolean;
}

/**
 * Compute the field patch + history row for an agreed-price edit. The agreed
 * `value` is in `currency` units; `seedGateAmounts` distributes it across the
 * tier's fixed money gates so the gate-expected figure equals the agreed price
 * in the agreed currency.
 */
export function buildAgreedPriceUpdate(input: AgreedPriceInput): AgreedPriceUpdate {
  const currency: Currency = input.currency === 'USD' ? 'USD' : 'INR';
  const value = Math.round(Number(input.value));
  const gst =
    typeof input.gst === 'boolean' ? input.gst : currency === 'USD' ? false : true;
  const firmId: FirmId = firmFor({ currency, gst });
  // NO-OP (Bug B) — same currency AND amount as the prior agreed price.
  // Both prior fields must be present (a first-time set is never a no-op).
  const noop =
    input.prior.currency != null &&
    input.prior.value != null &&
    input.prior.currency === currency &&
    Math.round(Number(input.prior.value)) === value;
  return {
    patch: {
      currency,
      valueINR: value,
      firmId,
      gst,
      // THE FIX — agreed value seeded across the tier's money gates in the
      // agreed currency. For a single-fixed-gate tier (L1A advance) this is
      // simply { advance: value }, so the gate expects $40 not ₹11,000.
      gateAmounts: seedGateAmounts(input.tier, value),
    },
    historyEntry: {
      at: input.now,
      by: input.actorName || 'Someone',
      fromCurrency: input.prior.currency,
      fromValue: input.prior.value,
      toCurrency: currency,
      toValue: value,
      ...(input.note && input.note.trim()
        ? { note: input.note.trim().slice(0, 200) }
        : {}),
    },
    noop,
  };
}
